import { FolderPrimaryListDto } from "@common/dto/folder/primary-list-dto";
import client, { clientTransformed } from "@common/utils/rest";

/**
 * Load datasheet format folders.
 *
 * - `context`:
 *    1 = individual
 *    2 = pedigree
 * - `tds`:
 *    true  = table datasheet
 *    false = normal datasheet
 * - `parentId`:
 *    Parent folder ID (defaults to 0 for root folders)
 *
 * If called without params it defaults to individual, non-table folders
 */
export const getDatasheetFolders = async (
  context: number = 1,
  tds: boolean = false,
  parentId: number = 0
): Promise<Array<FolderPrimaryListDto>> => {
  const response = await client({
    path: "api/folders/formats/ds",
    method: "GET",
    params: {
      context,
      tds,
      parentId,
    },
  });

  return (response).entity as Array<FolderPrimaryListDto>;
};

/**
 * Load TDS (table datasheet) format folders.
 * Equivalent to GWT's loadTDSFormatsFolders.
 *
 * - `context`:
 *    1 = individual (IDS_CONTEXT)
 *    2 = pedigree (PDS_CONTEXT)
 * - `parentId`:
 *    Parent folder ID (defaults to 0 for root folders)
 */
export const getTdsFormatFolders = async (
  context: number = 1,
  parentId: number = 0
): Promise<Array<FolderPrimaryListDto>> => {
  const response = await client({
    path: "api/folders/formats/ds",
    method: "GET",
    params: {
      context,
      tds: true, // Always true for TDS folders
      parentId,
    },
  });

  return (response).entity as Array<FolderPrimaryListDto>;
};

/**
 * Create a datasheet folder.
 */
export const createDatasheetFolder = async (params: {
  context: number;
  parentId?: number;
  name: string;
  tds?: boolean;
}) => {
  const { context, parentId, name, tds = false } = params;
  const response = await clientTransformed({
    path: "api/folders/ds",
    method: "POST",
    entity: {
      context,
      parentId,
      name,
      tds,
    },
  });
  return response.entity;
};

/**
 * Delete a datasheet folder.
 */
export const deleteDatasheetFolder = async (params: {
  folderId: number;
  tds?: boolean;
}) => {
  const { folderId, tds = false } = params;
  const response = await clientTransformed({
    path: "api/folders/ds/delete",
    method: "POST",
    params: {
      folderId,
      tds,
    },
  });
  return response.entity;
};